import translate from '@translate';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormBlock from 'src/components/FormBlock';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { UserType } from 'src/types/user';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import Detail from './Detail';
import TopBlock from './TopBlock';
import {
    clearUserDetail,
    createUserDetailFetch,
    getUserDetailFetch,
    updateUserDetailFetch
} from 'src/resources/userDetail/reducer';
import { getRolesApprovedFetch } from 'src/resources/rolesApproved/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { EDIT_KEYS, EditKeysType, STORAGE_KEY } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import UserProviders from './UserProviders';
import { getProvidersListFetch } from 'src/resources/providersList/reducer';
import removeEmptyStringValues from 'src/utils/removeEmptyStringValues';
import isDeepEqual from 'src/utils/isDeepEqual';
import { setStorageData } from 'src/resources/storage/reducer';
import { samePaths } from 'src/utils/checkPaths';
import { FormErrorsType, OnChangeType } from '../../types/global';
import useAllProvidersField from './useAllProvidersField';

const initData = { USER_PROVIDERS: [] };
const UserDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idUser } = useParams();

    const newUser = !idUser;

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.userDetail.data);
    const loading = useSelector((state: RootStoreType) => state.userDetail.loading);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const storageData = useSelector((state: RootStoreType) => state.storage.data[STORAGE_KEY]);
    const loggedSkladonUser = useSelector((state: RootStoreType) => state.user.data?.SKLADON_USER);
    const rolesApprovedData = useSelector((state: RootStoreType) => state.rolesApproved.data);
    const providersListData = useSelector((state: RootStoreType) => state.providersList.data);

    const [editData, setEditData] = useState<UserType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const editDataRef = useRef<UserType>({});

    const disableEdit = newUser ? !useCheckPermission('USER.CREATE') : !useCheckPermission('USER.UPDATE');
    const ableAllProviders = useCheckPermission(['USER.ALLPROVIDERS.EDIT', 'PROVIDER.LIST']);
    const providersEnabled = ableAllProviders && !!providersListData;

    const onChange: OnChangeType<EditKeysType> = useCallback((key, value, error) => {
        setEditData((prev) => ({ ...prev, [key]: value }));
        setEditErrors((prev) => ({ ...prev, [key]: error }));
    }, []);

    const {
        allProviders,
        setAllProviders,
        isSkladonUser,
        showAllProvidersField,
        allProvidersFieldDisabled,
        onChangeAllProviders,
    } = useAllProvidersField({
        onChange,
        editData,
        oreginalData: data,
        providersListData: providersListData || [],
        newUser,
        loggedSkladonUser,
        disableEdit,
        enabled: providersEnabled,
    });

    const backUrl = '/users/';

    useEffect(() => {
        if (idUser) {
            dispatch(setLocationParams({ name: translate('userDetail.title', { idUser }), backUrl }));
        } else {
            dispatch(setLocationParams({ name: translate('userDetail.titleNew'), backUrl }));
        }
    }, [idUser, backUrl, locationHistory]);

    useEffect(() => {
        if (idUser) {
            dispatch(getUserDetailFetch(idUser));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearUserDetail());
        };
    }, [idUser]);

    useEffect(() => {
        if (data) {
            setEditData(data);
            editDataRef.current = data;
        }

        if (!idUser && data?.USER_ID) {
            setThisStorageData(null);
            navigate('/users/' + String(data.USER_ID));
        }
    }, [data]);

    useEffect(() => {
        if (!rolesApprovedData) {
            dispatch(getRolesApprovedFetch());
        }
    }, [rolesApprovedData]);

    // set and load providers
    useEffect(() => {
        if (ableAllProviders && !providersListData) {
            dispatch(getProvidersListFetch({}));
        }
    }, [providersListData, ableAllProviders]);

    // save edit data -start
    useEffect(() => {
        if (
            newUser &&
            storageData &&
            // from receivings new or misclick and back 1 url
            (samePaths(locationHistory[0]?.pathname, '/users') || samePaths(locationHistory[1]?.pathname, '/users/new'))
        ) {
            setConfirmParams({
                title: translate('userDetail.confirmGetNewDataStorage'),
                content: translate('userDetail.confirmGetNewDataStorageText'),
                action: () => {
                    setEditData(storageData);
                },
                onCancel: () => {
                    setThisStorageData(null);
                }
            });
        }
        return () => {
            if (!editDataRef.current?.USER_ID) {
                if (isDeepEqual(editDataRef.current, initData)) {
                    setThisStorageData(null);
                } else {
                    setThisStorageData(editDataRef.current);
                }
            }
        };
    }, []);

    useEffect(() => {
        editDataRef.current = editData;
    }, [editData]);

    const setThisStorageData = (data: UserType | null) => {
        dispatch(setStorageData({ key: STORAGE_KEY, data }));
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            setConfirmParams({
                title: translate(newUser ? 'userDetail.create' : 'userDetail.save'),
                content: translate(newUser ? 'userDetail.createConfirm' : 'userDetail.saveConfirm'),
                action: () => {
                    const formatData: UserType = removeEmptyStringValues(
                        { ...editData },
                        {
                            PHONE: true,
                            COMPANY_ID: true,
                            TAX_ID: true,
                            STREET: true,
                            CITY: true,
                            STATE: true,
                            POST_CODE: true
                        }
                    );

                    if (newUser) {
                        dispatch(createUserDetailFetch({ user: formatData }));
                    } else {
                        dispatch(updateUserDetailFetch({ idUser, data: { user: formatData } }));
                    }
                }
            });
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText')
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <TopBlock data={data} idUser={idUser} newUser={newUser} disableEdit={disableEdit} handleSave={handleSave} />
            <form ref={formRef}>
                {loading && <Loading />}

                <FormBlock>
                    <Detail
                        onChange={onChange}
                        editData={editData}
                        editErrors={editErrors}
                        newUser={newUser}
                        disableEdit={disableEdit}
                        boolNumberCodes={boolNumberCodes}
                        loggedSkladonUser={loggedSkladonUser}
                        rolesApprovedData={rolesApprovedData}
                        data={data}
                        showAllProvidersField={showAllProvidersField}
                        allProviders={allProviders}
                        allProvidersFieldDisabled={allProvidersFieldDisabled}
                        onChangeAllProviders={onChangeAllProviders}
                    />
                    {providersEnabled && providersListData && (
                        <UserProviders
                            onChange={onChange}
                            editData={editData}
                            editErrors={editErrors}
                            oreginalData={data}
                            disableEdit={disableEdit}
                            providersListData={providersListData}
                            allProviders={allProviders}
                            setAllProviders={setAllProviders}
                            isSkladonUser={isSkladonUser}
                        />
                    )}
                </FormBlock>
            </form>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default UserDetailContainer;
